import React, { useState } from 'react';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import { Currency } from '@strava/units/react';
import SubscriptionFeatures from '../shared/SubscriptionFeatures';
import DurationSelectStacked from './DurationSelectStacked';
import DurationSelectSideBySide from '../Summary/DurationSelectSideBySide';
import Card from '../shared/Card';
import Inset from '../shared/Inset';
import PromoCodeInput from '../Summary/PricingList/PromoCodeInput';

import { getOrganicProduct } from '../../state/Checkout.actions';
import { useCheckoutContext } from '../../state/CheckoutContext';
import { getStyles as _getStyles } from '../../utils/checkoutUtils.js';
import { I18nPrefix } from '../../shared/constants';

import styles from './SummaryTrials.scss';

const I18nPrefixTrial = `${I18nPrefix}.summary.trial`;

export const calculateTrialEndDate = (trialLength, startDate) => {
  const trialEndDate = startDate ? new Date(startDate) : new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialLength);
  return Cldr.formatDateTime(trialEndDate, {
    format: 'additional',
    type: 'yMMMd'
  });
};

const renderPerMonthPrice = (price, currency) =>
  I18n.t_html(`${I18nPrefixTrial}.per_month`, {
    price: Cldr.formatCurrency(price, currency)
  });

const renderPerYearPrice = (price, currency) =>
  I18n.t_html(`${I18nPrefixTrial}.per_year`, {
    price: Cldr.formatCurrency(price, currency)
  });

export const SummaryTrials = () => {
  const { checkoutEntityState, dispatch } = useCheckoutContext();
  const { experiments, trialInfo, pricing } = checkoutEntityState;
  const {
    currency,
    billedToday,
    yearlyTotal,
    monthlyTotal,
    duration
  } = pricing;
  const { shouldShowTrustLanguage, shouldShowCheckoutRefresh } = experiments;

  const [showMoreBillingOptions, setShowMoreBillingOptions] = useState(false);
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false);

  const getStyles = (style) =>
    _getStyles(styles, style, shouldShowCheckoutRefresh);

  const toggleShowBillingOptions = () =>
    setShowMoreBillingOptions(!showMoreBillingOptions);

  const handlePromoClick = () =>
    shouldShowCheckoutRefresh
      ? setShowPromoCodeInput(true)
      : getOrganicProduct(checkoutEntityState, dispatch);

  const isYearlyDuration = duration === 'yearly';
  const totalPrimaryPrice = isYearlyDuration ? yearlyTotal : monthlyTotal;
  const renderOverview = () => {
    if (shouldShowTrustLanguage && trialInfo?.trialLength) {
      return (
        <span data-testid="disclaimer">
          {I18n.t_html(`${I18nPrefixTrial}.disclaimer`, {
            trialEndDate: calculateTrialEndDate(trialInfo.trialLength)
          })}
        </span>
      );
    }
    return <SubscriptionFeatures />;
  };

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const billingOptionsButtonText = I18n.t(
    `${I18nPrefixTrial}.${
      !showMoreBillingOptions ? 'more_billing_options' : 'less_billing_options'
    }`
  );
  const shouldShowDurationSelectSideBySide =
    showMoreBillingOptions && shouldShowCheckoutRefresh;
  const shouldShowDurationSelectStacked =
    showMoreBillingOptions && !shouldShowCheckoutRefresh;

  // In Checkout Refresh experiment, don't show "Less Billing Options"
  const shouldShowBillingOptions = shouldShowCheckoutRefresh
    ? !showMoreBillingOptions
    : true;
  const shouldShowDivider = shouldShowBillingOptions && !showPromoCodeInput;

  const billedTodayFormatted = shouldShowCheckoutRefresh ? (
    <Currency
      value={billedToday}
      currency={currency}
      minimumFractionDigits={0}
      maximumFractionDigits={0}
    /> // $0
  ) : (
    Cldr.formatCurrency(billedToday, currency) // $0.00
  );

  return (
    <div className={styles.checkoutSummary}>
      <Card className={clsx(getStyles('card'))} dataTestId="card">
        <Inset className={clsx(getStyles('overviewSection'))}>
          <h4 className={clsx(getStyles('overviewTitle'))}>
            {I18n.t(
              `${I18nPrefixTrial}.title${
                shouldShowCheckoutRefresh ? '_v2' : ''
              }`,
              {
                days: trialInfo?.trialLength
              }
            )}
          </h4>
          <p className={styles.overviewDescription}>{renderOverview()}</p>
        </Inset>

        {shouldShowDurationSelectSideBySide && (
          <Inset>
            <DurationSelectSideBySide />
          </Inset>
        )}

        <Inset
          className={clsx(getStyles('pricingSection'))}
          dataTestId="pricing-section"
        >
          <div
            className={clsx(getStyles('lineItem'), getStyles('billedToday'))}
          >
            <div className={styles.priceRow} data-testid="footer_billed_today">
              <span>{I18n.t(`${I18nPrefixTrial}.billed.today`)}</span>
              <span>{billedTodayFormatted}</span>
            </div>
          </div>

          <div
            className={clsx(
              getStyles('lineItem'),
              getStyles('billedAfterTrial')
            )}
            data-testid="billed-after-trial"
          >
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefixTrial}.billed.after_trial`)}</span>
              <span>{totalPrimaryText}</span>
            </div>
          </div>
        </Inset>

        <Inset className={styles.optionsSection} dataTestId="options-section">
          <div className={clsx(getStyles('moreBillingOptions'))}>
            {shouldShowBillingOptions && (
              <button
                className={clsx(
                  'btn',
                  'btn-link',
                  getStyles('moreBillingOptionsButton')
                )}
                type="button"
                data-testid="show-more-billing-options"
                onClick={toggleShowBillingOptions}
              >
                {billingOptionsButtonText}
              </button>
            )}
            {shouldShowDivider && (
              <span className={styles.actionDivider}>·</span>
            )}
            {showPromoCodeInput ? (
              <PromoCodeInput />
            ) : (
              <button
                className={clsx(
                  'btn',
                  'btn-link',
                  getStyles('moreBillingOptionsButton')
                )}
                onClick={handlePromoClick}
                type="button"
              >
                {I18n.t(
                  `${I18nPrefixTrial}.${
                    shouldShowCheckoutRefresh ? 'redeem' : 'add'
                  }_promo_code`
                )}
              </button>
            )}
          </div>

          {shouldShowDurationSelectStacked && <DurationSelectStacked />}
        </Inset>

        {!shouldShowCheckoutRefresh && (
          <Inset className={styles.footnoteSection}>
            {I18n.t(`${I18nPrefixTrial}.cancel`)}
          </Inset>
        )}
      </Card>
    </div>
  );
};

export default SummaryTrials;
